/**
 * Pin state and its witness — ADR-022 §4's second seam.
 *
 * ONE responsibility: hold the pins a person has placed on one hand, and
 * annotate each with what the volume saw there. Headless: no DOM, no engine, no
 * three. The panel renders these; the marker layer draws them; neither decides
 * anything about them here.
 *
 * THE POSTURE, in one line: **the branch is evidence about a pin, never
 * permission for one** (§3). A pin with a consensus cluster under it is
 * `corroborated` and says how far; a pin with nothing under it is `unsupported`
 * and STILL GRAFTS — that is a fused digit, a mitten, or Bart's sixth finger,
 * and refusing them was the dead end this ADR ends. The only thing that refuses
 * is a pin that is not on this hand at all (§9), which is a wiring error and is
 * checked at the gesture, not here.
 *
 * WHAT A PIN IS NOT SNAPPED TO. A free click stays exactly where the person put
 * it. It is tempting to slide it onto the nearest cluster tip — that place is
 * better measured — but a silent snap moves a person's answer, and the whole
 * ADR exists because answers were being moved by machinery they could not see.
 * Adopting a candidate is offered as its own gesture instead (`adoptCluster`),
 * where the person can see they are taking the measured place.
 *
 * Depends on: hands/tip-consensus (types + the shared cluster radius),
 * kernel/propose-digit-names (the ONE ordering rule, §7 gate 9).
 * Depended on by: shell/hands-panel(-state), viewport wiring in main.
 */
import { proposeNamesForTips, type DigitName } from '@harryhausen/kernel';
import { CLUSTER_RADIUS_FRACTION, type TipCluster, type TipConsensus } from './tip-consensus.js';
import type { Vec3 } from './hand-evidence.js';

/**
 * One pin: a place on the hand and the digit a person says is there.
 *
 * `digit: null` is a real state and not a half-finished one — a click somewhere
 * the analyzer never separated has no name to propose, so the pin exists,
 * visibly, waiting to be named. It contributes nothing to a graft until it is
 * (an unnamed pin is not a digit).
 */
export interface HandPin {
  /** `pin-N`, minted on creation — the DOM key and the marker's identity. */
  readonly id: string;
  /** BIND-WORLD, exactly where the click landed (or the cluster tip, if adopted). */
  readonly tip: Vec3;
  /**
   * The OPTIONAL second row target (ADR-024 §2.2): this digit's first knuckle,
   * BIND-WORLD, exactly where the click landed — the same no-silent-snap rule
   * as the tip (the kernel seats it into the tube at graft time; the stored
   * point stays the person's own). `null` until someone places one. It belongs
   * to the PLACE: it survives a re-name (the finger did not move), and goes
   * away with the pin.
   */
  readonly knuckle: Vec3 | null;
  readonly digit: DigitName | null;
  /**
   * Where the NAME came from. `proposed` is the analyzer's pre-fill (D4) and
   * must render visibly as a proposal; `human` is a person's own statement.
   * The distinction is the condition D4 came with, so it is carried in the data
   * rather than reconstructed by a surface.
   */
  readonly named: 'human' | 'proposed';
  /** The consensus candidate this pin was adopted from, if any. Never a placement input. */
  readonly clusterId: string | null;
}

export interface PinSet {
  readonly pins: readonly HandPin[];
  /** Monotonic mint counter — ids never reused, so a re-pinned digit is a NEW pin. */
  readonly minted: number;
  /**
   * Candidates a person has thrown out — the cuff rim, the kobold's phantom
   * finger, the dorsal arch. THE OLD "not a digit", BACK BY FIELD REQUEST
   * (Allen, 2026-08-12: *"i miss the old, not a digit … or just an x to dump
   * that marker — reject it. if they do it on accident, they could retrieve
   * it"*). ADR-022 §6 was right that it must not GATE the graft, and wrong that
   * nobody would want to say it: a discarded candidate stops cluttering the
   * model, and being able to take it back is what makes discarding it cheap.
   */
  readonly rejected: readonly string[];
}

export const emptyPinSet: PinSet = { pins: [], minted: 0, rejected: [] };

/** What the volume saw where a pin was placed. Evidence, never permission (§3). */
export type PinWitness =
  | {
      readonly kind: 'corroborated';
      readonly clusterId: string;
      /** World-unit distance from the pin to the cluster's tip. */
      readonly distance: number;
      readonly votes: number;
      readonly resolutions: readonly number[];
    }
  | {
      readonly kind: 'unsupported';
      /** The radius searched, world units — so "nothing there" carries its scale. */
      readonly searchRadius: number;
    };

const dist = (a: Vec3, b: Vec3): number => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

/**
 * Add a pin. A digit already worn by another pin MOVES here — one digit, one
 * place — because the alternative is a duplicate the Document grammar refuses
 * after the fact (`digitAssignment` values must be unique per hand). The
 * person's last statement wins, and the pin it was taken from goes back to
 * unnamed rather than vanishing: they pointed at that place on purpose.
 */
export function addPin(
  set: PinSet,
  pin: { readonly tip: Vec3; readonly digit: DigitName | null; readonly named?: 'human' | 'proposed'; readonly clusterId?: string | null },
): PinSet {
  const minted = set.minted + 1;
  const fresh: HandPin = {
    id: `pin-${minted}`,
    tip: pin.tip,
    knuckle: null,
    digit: pin.digit,
    named: pin.named ?? 'human',
    clusterId: pin.clusterId ?? null,
  };
  return { ...set, minted, pins: [...unname(set.pins, fresh.digit, fresh.id), fresh] };
}

/**
 * Place (or clear, with null) a pin's knuckle — ADR-024 §2.2's second row.
 * Addressed by the pin, not the digit, so a knuckle can only ever land on a
 * place a person already vouched for; the flyout offers the row only for
 * digits that have a named pin.
 */
export function setKnuckle(set: PinSet, id: string, knuckle: Vec3 | null): PinSet {
  if (!set.pins.some((p) => p.id === id)) return set;
  return { ...set, pins: set.pins.map((p) => (p.id === id ? { ...p, knuckle } : p)) };
}

/** Rename (or un-name) a pin. Same move-the-digit rule as {@link addPin}. */
export function namePin(set: PinSet, id: string, digit: DigitName | null): PinSet {
  const target = set.pins.find((p) => p.id === id);
  if (target === undefined) return set;
  return {
    ...set,
    pins: unname(set.pins, digit, id).map((p) => (p.id === id ? { ...p, digit, named: 'human' as const } : p)),
  };
}

export function removePin(set: PinSet, id: string): PinSet {
  return { ...set, pins: set.pins.filter((p) => p.id !== id) };
}

/**
 * Adopt a consensus candidate as a pin, at the CLUSTER'S OWN TIP — a
 * cross-resolution mean, which is a better-measured place than a click on the
 * skin, and the person can see that is what they are taking.
 */
export function adoptCluster(set: PinSet, cluster: TipCluster, digit: DigitName | null, named: 'human' | 'proposed'): PinSet {
  return addPin(set, { tip: cluster.tip, digit, named, clusterId: cluster.id });
}

/** Throw a candidate out — it stops being drawn and stops being nudged about. */
export function rejectCluster(set: PinSet, clusterId: string): PinSet {
  return set.rejected.includes(clusterId) ? set : { ...set, rejected: [...set.rejected, clusterId] };
}

/** Take it back. An accident costs one click, which is why rejecting can be one click. */
export function restoreCluster(set: PinSet, clusterId: string): PinSet {
  return { ...set, rejected: set.rejected.filter((id) => id !== clusterId) };
}

/** Pins that carry a digit — the only ones that graft. */
export function namedPins(set: PinSet): readonly HandPin[] {
  return set.pins.filter((p) => p.digit !== null);
}

/** Clusters nobody pinned, adopted, or threw out — the ghost dots, and the nudge's raw material. */
export function unpinnedClusters(set: PinSet, scan: TipConsensus): readonly TipCluster[] {
  const claimed = new Set(set.pins.map((p) => p.clusterId).filter((id): id is string => id !== null));
  const radius = CLUSTER_RADIUS_FRACTION * scan.span;
  return scan.clusters.filter(
    (c) =>
      !claimed.has(c.id) &&
      !set.rejected.includes(c.id) &&
      !set.pins.some((p) => p.clusterId === null && dist(p.tip, c.tip) <= radius),
  );
}

/**
 * What the volume says about one pin. The search radius is the consensus
 * clustering radius — the same fraction of the hand's span that decides whether
 * two tips are the same digit everywhere else in this codebase, so "near enough"
 * means one thing (the run-document witness uses it too, in voxel space).
 */
export function witnessPin(pin: HandPin, scan: TipConsensus): PinWitness {
  const radius = CLUSTER_RADIUS_FRACTION * scan.span;
  let best: TipCluster | null = null;
  let bestD = radius;
  for (const cluster of scan.clusters) {
    const d = dist(pin.tip, cluster.tip);
    if (d < bestD) {
      bestD = d;
      best = cluster;
    }
  }
  if (best === null) return { kind: 'unsupported', searchRadius: radius };
  return {
    kind: 'corroborated',
    clusterId: best.id,
    distance: bestD,
    votes: best.votes,
    resolutions: best.resolutions,
  };
}

/**
 * THE PRE-FILL (D4, §8) — **PARKED 2026-08-12, and not deleted.**
 *
 * It proposes a digit name for every consensus candidate. It has NO CONSUMER
 * today, and that is a decision rather than rot: Allen's second field round
 * found the names wrong often enough to cost time (*"i didn't love the pin as
 * index, pin as mid — because they were wrong, and it made me hesitate"*), and
 * the surface that replaced it — a bar of names that flies out at the disc —
 * wants a STABLE anatomical order more than a ranked one, because a control
 * whose buttons move is a control you have to read every time.
 *
 * It stays because the reason it was wrong is understood and fixable: §8.1 —
 * the ordering can only be anchored on the analyzer's thumb, and a measured
 * palmar/dorsal cue would give it the invariant form §8 actually specified.
 * When that lands, this is what consumes it. Deleting it would delete the
 * measurement too.
 *
 * The anchor is the analyzer's own §4 thumb pre-fill, mapped from the branch it
 * names at the current resolution to the cluster that resolves there. That
 * anchor is margin-guarded at the source (a ≥2-level parting margin AND
 * divergence past the direction-uncertainty bound, or it returns null), and
 * when it is absent this returns NOTHING rather than ordering a row off an
 * arbitrary end. A pre-fill that guesses is worse than no pre-fill: it teaches
 * a person to stop reading it.
 *
 * The ordering itself lives in the kernel and is shared with the Document path
 * (`proposeNamesForTips` → `nameAroundThumb`), so the pin surface and the graft
 * cannot come to disagree about which end of the row the thumb is on.
 */
export function prefillNames(
  scan: TipConsensus,
  thumbClusterId: string | null,
): { readonly names: Readonly<Record<string, DigitName>>; readonly confident: boolean } {
  if (thumbClusterId === null || scan.clusters.length === 0) return { names: {}, confident: false };
  const proposal = proposeNamesForTips(
    scan.clusters.map((c) => ({ id: c.id, at: c.tip })),
    { thumbId: thumbClusterId },
  );
  return { names: proposal.assignment, confident: proposal.confident };
}

/**
 * The cluster a branch id resolves to — the bridge the pre-fill crosses to get
 * from the analyzer's thumb (`branch-N` at one resolution) to a cluster id.
 * Returns null when this resolution's thumb is not in the consensus at all,
 * which is a real outcome and produces no pre-fill rather than a wrong one.
 */
export function clusterForBranch(
  branchId: string | null,
  mapping: ReadonlyMap<string, string>,
): string | null {
  if (branchId === null) return null;
  for (const [clusterId, branch] of mapping) if (branch === branchId) return clusterId;
  return null;
}

/** Digits nobody has used yet — the only names the surface should still offer. */
export function freeDigits(set: PinSet, all: readonly DigitName[]): readonly DigitName[] {
  const worn = new Set(set.pins.map((p) => p.digit));
  return all.filter((d) => !worn.has(d));
}

/**
 * Un-name any pin whose digit is no longer part of this hand's anatomy.
 *
 * SAYING "THIS HAND HAS THREE DIGITS" IS A STATEMENT ABOUT ANATOMY, and it can
 * invalidate a name already given (Codex P1, #94). A hand of three offers
 * Thumb · Index · **Pinky** — the ulnar-most finger is always the Pinky
 * (Allen's kobold ruling) — so a pin called `Middle` is not merely surplus, it
 * is a name that hand does not have. Left alone it would satisfy a count-based
 * completeness check and graft ring motion onto an outer finger.
 *
 * The pin SURVIVES and goes back to unnamed: the person pointed at that place
 * on purpose, and the thing that expired is the label, not the place. It shows
 * as an open question in the list, which is where a re-name belongs.
 */
export function reconcileNames(set: PinSet, offered: readonly DigitName[]): PinSet {
  const allowed = new Set(offered);
  if (set.pins.every((p) => p.digit === null || allowed.has(p.digit))) return set;
  return { ...set, pins: set.pins.map((p) => (p.digit !== null && !allowed.has(p.digit) ? { ...p, digit: null } : p)) };
}

/** Take a digit off whatever pin is wearing it, so one digit names one place. */
function unname(pins: readonly HandPin[], digit: DigitName | null, exceptId: string): readonly HandPin[] {
  if (digit === null) return pins;
  return pins.map((p) => (p.id !== exceptId && p.digit === digit ? { ...p, digit: null } : p));
}
